import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Mail, MapPin, Phone, ShieldCheck, Clock, Globe2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const OFFICES = [
    { city: "Singapore", addr: "Guoco Tower, 1 Wallich Street · #14-01", role: "HQ · Revenue & Product", flag: "🇸🇬" },
    { city: "Jakarta", addr: "Menara BCA, Jl. MH Thamrin No.1 · 38th floor", role: "Engineering & ML", flag: "🇮🇩" },
    { city: "Ho Chi Minh City", addr: "Deutsches Haus, 33 Le Duan · 22nd floor", role: "ML Research", flag: "🇻🇳" },
];

const TOPICS = [
    { k: "demo", l: "Book a product demo" },
    { k: "sales", l: "Talk to sales" },
    { k: "partners", l: "Partnership / integrations" },
    { k: "support", l: "Technical support" },
    { k: "careers", l: "Careers & roles" },
    { k: "press", l: "Press & media" },
];

const VOLUMES = ["Under 1,000", "1,000 – 10,000", "10,000 – 100,000", "100,000+"];

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        role: "",
        volume: "",
        topic: "demo",
        message: "",
    });
    const [status, setStatus] = useState("idle"); // idle | loading | sent | error

    const API_BASE = process.env.REACT_APP_API_URL || "https://analysis.crednx.com";

    const onSubmit = async (e) => {
        e.preventDefault();
        setStatus("loading");
        try {
            const res = await fetch(`${API_BASE}/auth/v1/contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (!res.ok) throw new Error("Server error");
            setStatus("sent");
        } catch {
            setStatus("error");
        }
    };

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

    return (
        <div data-testid="page-contact">
            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-hero pointer-events-none" />
                <div className="absolute inset-0 bg-grid-subtle bg-grid-fade opacity-55 pointer-events-none" />
                <div className="relative container max-w-[1240px] mx-auto px-6 pt-20 md:pt-28 pb-14">
                    <p className="text-[12px] tracking-[0.22em] uppercase text-muted-foreground">Contact</p>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-5 font-serif text-[48px] sm:text-[64px] lg:text-[78px] leading-[0.98] tracking-[-0.035em] text-balance max-w-[960px]"
                    >
                        Let&apos;s see your statements{" "}
                        <span className="italic">decoded</span>{" "}
                        in thirty minutes.
                    </motion.h1>
                    <p className="mt-8 text-[17px] md:text-[19px] leading-[1.6] text-muted-foreground max-w-[640px]">
                        Bring five live statements. We&apos;ll parse them on-screen and walk your risk team through the exact output you&apos;d get in production.
                    </p>
                </div>
            </section>

            {/* Form + sidecar */}
            <section className="relative">
                <div className="container max-w-[1240px] mx-auto px-6 pb-24 md:pb-32 grid lg:grid-cols-12 gap-10 lg:gap-16">
                    {/* Form */}
                    <div className="lg:col-span-7">
                        <div className="rounded-3xl border border-border bg-card p-8 md:p-10 shadow-elegant">
                            {status === "sent" ? (
                                <SentState
                                    onReset={() => {
                                        setStatus("idle");
                                        setForm({ name: "", email: "", company: "", role: "", volume: "", topic: "demo", message: "" });
                                    }}
                                />
                            ) : status === "error" ? (
                                <div className="text-center py-12">
                                    <p className="text-destructive font-medium mb-4">Something went wrong. Please try again.</p>
                                    <button onClick={() => setStatus("idle")} className="text-[14px] underline text-primary">Go back</button>
                                </div>
                            ) : (
                                <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form">
                                    <Row>
                                        <Field label="Full name" required>
                                            <input
                                                value={form.name}
                                                onChange={set("name")}
                                                required
                                                type="text"
                                                placeholder="Ayu Pradnyadari"
                                                data-testid="input-name"
                                                className={inputCn}
                                            />
                                        </Field>
                                        <Field label="Work email" required>
                                            <input
                                                value={form.email}
                                                onChange={set("email")}
                                                required
                                                type="email"
                                                placeholder="ayu@lender.co.id"
                                                data-testid="input-email"
                                                className={inputCn}
                                            />
                                        </Field>
                                    </Row>
                                    <Row>
                                        <Field label="Company">
                                            <input
                                                value={form.company}
                                                onChange={set("company")}
                                                type="text"
                                                placeholder="PT Lender Digital"
                                                data-testid="input-company"
                                                className={inputCn}
                                            />
                                        </Field>
                                        <Field label="Your role">
                                            <input
                                                value={form.role}
                                                onChange={set("role")}
                                                type="text"
                                                placeholder="Head of Risk"
                                                data-testid="input-role"
                                                className={inputCn}
                                            />
                                        </Field>
                                    </Row>
                                    <Row>
                                        <Field label="Monthly statement volume">
                                            <Select
                                                value={form.volume}
                                                onValueChange={setField("volume")}
                                            >
                                                <SelectTrigger
                                                    data-testid="input-volume"
                                                    className={selectTriggerCn}
                                                    aria-label="Monthly statement volume"
                                                >
                                                    <SelectValue placeholder="Select volume…" />
                                                </SelectTrigger>
                                                <SelectContent className="rounded-xl border-border">
                                                    {VOLUMES.map((v) => (
                                                        <SelectItem
                                                            key={v}
                                                            value={v}
                                                            className="text-[14px]"
                                                            data-testid={`volume-option-${v.replace(/\s+/g, "-").toLowerCase()}`}
                                                        >
                                                            {v}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </Field>
                                        <Field label="What's this about?">
                                            <Select
                                                value={form.topic}
                                                onValueChange={setField("topic")}
                                            >
                                                <SelectTrigger
                                                    data-testid="input-topic"
                                                    className={selectTriggerCn}
                                                    aria-label="Topic"
                                                >
                                                    <SelectValue placeholder="Select topic…" />
                                                </SelectTrigger>
                                                <SelectContent className="rounded-xl border-border">
                                                    {TOPICS.map((t) => (
                                                        <SelectItem
                                                            key={t.k}
                                                            value={t.k}
                                                            className="text-[14px]"
                                                            data-testid={`topic-option-${t.k}`}
                                                        >
                                                            {t.l}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </Field>
                                    </Row>
                                    <Field label="Anything we should know">
                                        <textarea
                                            value={form.message}
                                            onChange={set("message")}
                                            rows={5}
                                            placeholder="Our team currently reviews ~8,000 Indonesian statements/month manually. We'd like to see parse quality on Mandiri and BCA formats."
                                            data-testid="input-message"
                                            className={cn(inputCn, "resize-none")}
                                        />
                                    </Field>

                                    <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                                        <p className="text-[12px] text-muted-foreground inline-flex items-center gap-2">
                                            <ShieldCheck className="w-3.5 h-3.5 text-primary" /> We&apos;ll never share your details. NDA on request.
                                        </p>
                                        <Button
                                            type="submit"
                                            variant="hero"
                                            size="lg"
                                            disabled={status === "loading"}
                                            data-testid="contact-submit"
                                        >
                                            {status === "loading" ? "Sending…" : "Send message"}
                                            <ArrowUpRight className="w-4 h-4 ml-1" />
                                        </Button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>

                    {/* Sidecar */}
                    <aside className="lg:col-span-5 space-y-4">
                        <div className="rounded-2xl border border-border bg-card p-7">
                            <p className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-medium">Direct lines</p>
                            <div className="mt-5 space-y-4">
                                {[
                                    { icon: Mail, label: "Sales", value: "sales@crednx.com" },
                                    { icon: Mail, label: "Support", value: "support@crednx.com" },
                                    { icon: Phone, label: "APAC", value: "+65 3163 4800" },
                                ].map((c) => (
                                    <div key={c.label} className="flex items-center gap-4">
                                        <div className="w-9 h-9 rounded-lg bg-primary/5 border border-primary/10 text-primary inline-flex items-center justify-center">
                                            <c.icon className="w-4 h-4" />
                                        </div>
                                        <div>
                                            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{c.label}</p>
                                            <p className="text-[14.5px] font-medium text-foreground">{c.value}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="rounded-2xl border border-border bg-gradient-hero p-7 overflow-hidden relative">
                            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-accent/20 blur-3xl pointer-events-none" />
                            <div className="relative">
                                <p className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-medium">What to expect</p>
                                <ul className="mt-5 space-y-3.5">
                                    {[
                                        "Response within one business day",
                                        "Scoping call (30 min) with a product engineer",
                                        "Optional live parse of five of your statements",
                                        "NDA signed before any live data is shared",
                                    ].map((t) => (
                                        <li key={t} className="flex items-start gap-3 text-[14px] text-foreground/85">
                                            <Check className="w-4 h-4 mt-0.5 text-primary shrink-0" /> {t}
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-6 pt-5 border-t border-border/70 flex items-center gap-3 text-[12.5px] text-muted-foreground">
                                    <Clock className="w-4 h-4 text-primary" />
                                    Average first response: <span className="font-medium text-foreground">3h 12m</span>
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
            </section>

            {/* Offices */}
            <section className="relative bg-surface-deep text-surface-deep-fg overflow-hidden">
                <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
                <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-accent/10 blur-3xl pointer-events-none" />

                <div className="relative container max-w-[1240px] mx-auto px-6 py-24 md:py-28">
                    <div className="max-w-[680px] mb-14">
                        <p className="text-[12px] tracking-[0.22em] uppercase text-accent/90 inline-flex items-center gap-2">
                            <Globe2 className="w-3.5 h-3.5" /> Offices
                        </p>
                        <h2 className="mt-4 font-serif text-[38px] md:text-[54px] leading-[1.02] tracking-tight text-balance">
                            Three time zones.{" "}
                            <span className="italic text-surface-deep-fg/75">Zero latency.</span>
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-5">
                        {OFFICES.map((o, i) => (
                            <motion.div
                                key={o.city}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                                className="rounded-2xl border border-surface-deep-fg/10 bg-surface-deep-fg/[0.03] p-7 hover:bg-surface-deep-fg/[0.06] transition-colors"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-[26px] leading-none">{o.flag}</span>
                                    <span className="text-[11px] uppercase tracking-[0.16em] text-surface-deep-fg/55">{o.role}</span>
                                </div>
                                <p className="mt-6 font-serif text-[28px] tracking-tight leading-tight">{o.city}</p>
                                <p className="mt-3 text-[13.5px] text-surface-deep-fg/65 leading-relaxed inline-flex items-start gap-2">
                                    <MapPin className="w-4 h-4 mt-0.5 text-accent shrink-0" /> {o.addr}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}

const inputCn =
    "w-full rounded-xl bg-background-soft/70 border border-border hover:border-primary/30 focus:border-primary focus:ring-2 focus:ring-primary/15 focus:outline-none px-4 py-3 text-[14.5px] text-foreground placeholder:text-muted-foreground/70 transition-colors";

// Shadcn Select trigger styled to match native inputs in this form
const selectTriggerCn =
    "w-full h-auto rounded-xl bg-background-soft/70 border-border hover:border-primary/30 focus:border-primary focus:ring-2 focus:ring-primary/15 focus:ring-offset-0 shadow-none px-4 py-3 text-[14.5px] text-foreground data-[placeholder]:text-muted-foreground/70 transition-colors";

function Row({ children }) {
    return <div className="grid md:grid-cols-2 gap-5">{children}</div>;
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-[12px] tracking-wider uppercase text-muted-foreground font-medium">
                {label}
                {required && <span className="text-destructive ml-1">*</span>}
            </span>
            <div className="mt-2">{children}</div>
        </label>
    );
}

function SentState({ onReset }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="py-10 text-center"
            data-testid="contact-sent-state"
        >
            <div className="mx-auto w-14 h-14 rounded-full bg-accent/20 border border-accent/30 text-accent-foreground inline-flex items-center justify-center">
                <Check className="w-6 h-6 text-primary" />
            </div>
            <h3 className="mt-6 font-serif text-[30px] tracking-tight">Message sent.</h3>
            <p className="mt-3 text-[15px] text-muted-foreground max-w-md mx-auto">
                We&apos;ll come back to you within one business day — usually faster. In the meantime, explore our live BSA product.
            </p>
            <div className="mt-7 flex items-center justify-center gap-3">
                <Button variant="outline-dark" size="lg" onClick={onReset}>
                    Send another
                </Button>
            </div>
        </motion.div>
    );
}
